import React, { useEffect, useState } from 'react';
import { Card, Footer, PageLayout } from '@bharatpe/bharat-ui';
import { useDeviceSimList, useOnBoardingRedirection, useSelectedSim } from 'Slices';
import { IDispatcherType } from 'types';
import { useDispatch } from 'react-redux';
import history from 'utils/History';
import SimSelection from './SimSelection';
import { triggerSettlementAccountApi, useSettlementAccount } from 'Slices/GetSettlementAccount';
import AppHeader from 'components/atoms/AppHeader';
import BankIcon from 'assets/icons/Autopay/bank.svg';
import SkeletonUI from '@bharatpe/bharat-ui/web/SkeletonUI';
import { getURLParameter } from 'utils/nativeSupport';
import { openDeepLinkWithCallback } from 'utils/nativeUpiHandler';
import Toasty from 'components/atoms/Toasty';
import { isEmpty } from 'utils/Utils';
import { triggerCustomerSettlementAccountApi, useCustomerSettlementAccount } from 'Slices/GetCustomerSettlementAccount';
import { ONBOARDING_REDIRECTION_PAGE } from 'utils/CONSTANT';
import { deviceBindingRedirectionHandle } from 'utils/deviceBindingHelper';
import { logAnalyticsEvent } from 'utils/AnalyticsEvent';
import { useLanguageConfig } from 'Slices/LanguageConfigSlice';

const SimBinding = () => {
	const dispatch = useDispatch<IDispatcherType>();
	const [simSelected, setSimSelected] = useState<number>(0);
	const [bankDetails, setBankDetails] = useState<any>({});
	const [sim, setSim] = useState<any>(null);
	const settlementAccount = useSettlementAccount();
	const customerSettlementAccount = useCustomerSettlementAccount();

	const deviceSimList = useDeviceSimList();
	const attemptedSim = useSelectedSim();
	const onBoardingRedirection = useOnBoardingRedirection();
	const { languageData } = useLanguageConfig();

	useEffect(() => {
		logAnalyticsEvent({
			trigger_point: 'Page View',
			Product: 'AutoPayUPI',
			Flow: 'SetupUPI',
			Screen: 'Setup',
			Module: 'SimSelectionCard',
			Action: 'Viewed',
		});
	}, []);
	useEffect(() => {
		switch (onBoardingRedirection.source) {
			case ONBOARDING_REDIRECTION_PAGE.PROFILE:
				dispatch(triggerCustomerSettlementAccountApi());

				break;
			case ONBOARDING_REDIRECTION_PAGE.MANDATE:
				dispatch(triggerSettlementAccountApi());
				break;
		}
	}, [dispatch]);

	useEffect(() => {
		switch (onBoardingRedirection.source) {
			case ONBOARDING_REDIRECTION_PAGE.PROFILE:
				if (customerSettlementAccount.data) setBankDetails(customerSettlementAccount?.data?.data);
				else {
					const redirectUri = getURLParameter(window.location.href, 'redirectURIDeeplink');
					openDeepLinkWithCallback(redirectUri, () => {
						Toasty.error('Something went wrong');
					});
				}

				break;
			case ONBOARDING_REDIRECTION_PAGE.MANDATE:
				if (settlementAccount.data) setBankDetails(settlementAccount?.data?.data);
				else {
					const redirectUri = getURLParameter(window.location.href, 'redirectURIDeeplink');
					openDeepLinkWithCallback(redirectUri, () => {
						Toasty.error('Something went wrong');
					});
				}
				break;
		}
	}, [settlementAccount, customerSettlementAccount, onBoardingRedirection]);

	const onClickContinue = () => {
		logAnalyticsEvent({
			trigger_point: `Click on 'SETUP AUTOPAY'`,
			Product: 'AutoPayUPI',
			Flow: 'SetupUPI',
			Screen: 'Setup',
			Module: 'Continue',
			Element: 'CTA',
			Action: 'Tapped',
			ctaType: 'continue',
		});
		history.push({ pathname: '/upi/upi-activation-status', state: { selectedSim: sim } });
	};

	const backEventAction = () => {
		logAnalyticsEvent({
			trigger_point: `Clicks on back button`,
			Product: 'AutoPayUPI',
			Flow: 'SetupUPI',
			Screen: 'Setup',
			Element: 'back',
			Action: 'Tapped',
			source: 'appHeader',
		});
	};

	useEffect(() => {
		if (!isEmpty(deviceSimList)) {
			setSim(deviceSimList?.[simSelected]);
		}
	}, [simSelected]);

	useEffect(() => {
		if (isEmpty(deviceSimList)) {
			Toasty.error(languageData.NO_SIM_FOUND_TRY_AFTER_INSERTING_SIM);
			deviceBindingRedirectionHandle(true);
			return;
		}
	}, [deviceSimList]);

	return (
		<PageLayout
			className="pb18 white-background"
			footerContent={<Footer primaryActionText={languageData.CONTINUE} primaryAction={onClickContinue} />}>
			<>
				<div className="mt8">
					<AppHeader title={''} backEventAction={() => backEventAction()} />
				</div>

				<div className="content-center mt8 mb4">
					<p className="Bold500">{languageData.SELECT_SIM}</p>
				</div>
				<div className="content-center ">
					<p className="regular400 grey-dark">
						{languageData.KINDLY_ENSURE_TO_SELECT_SIM_WHICH_IS_REGISTERED_WITH_YOUR}
					</p>
				</div>
				<div className="content-center" style={{ margin: '5px 20px ' }}>
					<p className="regular400 grey-dark">{languageData.FOLLOWING_BANK_DETAILS}</p>
				</div>
				<div className="m5">
					{bankDetails ? (
						<Card
							strip={{
								text: languageData.SELECT_THE_SIM_LINKED_ONLY_TO_THIS_BANK_ACCOUNT,
							}}>
							<div className="flex flex-row">
								<img src={bankDetails ? bankDetails?.logo : BankIcon} width={'32px'} height={'32px'} />
								<div className="content-center mx5">
									<div>
										<p className="Light200 uppercase grey-dark">{bankDetails?.name || ' '}</p>
										<p className="SemiBold200">{`XXXX XXXX ${bankDetails ? bankDetails?.account?.slice(-4) : ' '}`} </p>
									</div>
								</div>
							</div>
						</Card>
					) : (
						<SkeletonUI
							variant="announcement-card"
							className="br2 mb3"
							options={{ width: '100%', height: 30, variant: 'rect' }}
						/>
					)}

					{!isEmpty(deviceSimList) ? (
						deviceSimList?.map((simDetails: any, index: number) => {
							return (
								<SimSelection
									simDetails={simDetails}
									index={index}
									key={simDetails?.sim_id}
									simSelected={simSelected}
									setSimSelected={setSimSelected}
									simFailed={attemptedSim === simDetails?.phone?.slice(-10)}
								/>
							);
						})
					) : (
						<></>
					)}
				</div>
			</>
		</PageLayout>
	);
};

export default SimBinding;
